import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ConfirmDialog,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../components/ui';
import { api, fieldErrors } from '../lib/api';

interface Testimonial {
  id: string;
  quote: string;
  authorName: string;
  authorTitle: string | null;
  rating: number;
  isActive: boolean;
  sortOrder: number;
}

interface SiteStat {
  id: string;
  icon: string;
  value: string;
  label: string;
  isActive: boolean;
  sortOrder: number;
}

const BLANK_STAT = { id: '', icon: '◕', value: '', label: '', isActive: true, sortOrder: 0 };

/** Offered in the picker. Plain glyphs, so nothing here needs an icon font. */
const STAT_ICONS = ['◕', '⌂', '◱', '★', '◆', '▲', '✦', '⧉', '₹', '☺'] as const;

/** A new row's starting point, and what "Cancel" discards. */
const BLANK = {
  id: '',
  quote: '',
  authorName: '',
  authorTitle: '',
  rating: 5,
  isActive: true,
  sortOrder: 0,
};

const Stars = ({ rating }: { rating: number }) => (
  <span aria-label={`${rating} out of 5`} style={{ color: 'var(--warn, #f59e0b)', letterSpacing: 1 }}>
    {'★'.repeat(rating)}
    <span className="dim">{'★'.repeat(5 - rating)}</span>
  </span>
);

export function PlatformLandingPage() {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [editing, setEditing] = useState<typeof BLANK | null>(null);
  const [removing, setRemoving] = useState<Testimonial | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editingStat, setEditingStat] = useState<typeof BLANK_STAT | null>(null);
  const [removingStat, setRemovingStat] = useState<SiteStat | null>(null);
  const [statErrors, setStatErrors] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['platform', 'testimonials'],
    queryFn: () => api.get<Testimonial[]>('/api/platform/testimonials'),
  });

  const stats = useQuery({
    queryKey: ['platform', 'site-stats'],
    queryFn: () => api.get<SiteStat[]>('/api/platform/site-stats'),
  });

  const refresh = () => client.invalidateQueries({ queryKey: ['platform', 'testimonials'] });
  const refreshStats = () => client.invalidateQueries({ queryKey: ['platform', 'site-stats'] });

  const saveStat = useMutation({
    mutationFn: (row: typeof BLANK_STAT) => {
      const payload = {
        icon: row.icon,
        value: row.value,
        label: row.label,
        isActive: row.isActive,
        sortOrder: Number(row.sortOrder),
      };
      return row.id
        ? api.patch(`/api/platform/site-stats/${row.id}`, payload)
        : api.post('/api/platform/site-stats', payload);
    },
    onSuccess: (_data, row) => {
      push(row.id ? 'Figure updated' : 'Figure added', 'ok');
      setEditingStat(null);
      setStatErrors({});
      void refreshStats();
    },
    onError: (caught) => {
      setStatErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const removeStat = useMutation({
    mutationFn: (row: SiteStat) => api.delete(`/api/platform/site-stats/${row.id}`),
    onSuccess: () => {
      push('Figure removed', 'ok');
      setRemovingStat(null);
      void refreshStats();
    },
    onError: fail,
  });

  const save = useMutation({
    mutationFn: (row: typeof BLANK) => {
      const payload = {
        quote: row.quote,
        authorName: row.authorName,
        authorTitle: row.authorTitle,
        rating: Number(row.rating),
        isActive: row.isActive,
        sortOrder: Number(row.sortOrder),
      };
      return row.id
        ? api.patch(`/api/platform/testimonials/${row.id}`, payload)
        : api.post('/api/platform/testimonials', payload);
    },
    onSuccess: (_data, row) => {
      push(row.id ? 'Testimonial updated' : 'Testimonial added', 'ok');
      setEditing(null);
      setErrors({});
      void refresh();
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const remove = useMutation({
    mutationFn: (row: Testimonial) => api.delete(`/api/platform/testimonials/${row.id}`),
    onSuccess: () => {
      push('Testimonial removed', 'ok');
      setRemoving(null);
      void refresh();
    },
    onError: fail,
  });

  // Each card reports its own state rather than one early return for the page:
  // the two lists are independent, and a failure to load the quotes should not
  // hide the figures as well.
  const rows = query.data ?? [];

  const statRows = stats.data ?? [];

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>Headline figures</h2>
            <div className="small muted">
              The numbers across the registration page. Claims about the business, not live counts —
              whatever you type here is what visitors read.
            </div>
          </div>
          <button className="btn primary" onClick={() => setEditingStat({ ...BLANK_STAT })}>
            + New figure
          </button>
        </div>

        {stats.isLoading ? (
          <Loading />
        ) : statRows.length === 0 ? (
          <Empty message="No figures yet — the strip is hidden until there is one." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Icon</th>
                  <th>Figure</th>
                  <th>Label</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {statRows.map((row) => (
                  <tr key={row.id}>
                    <td className="num dim">{row.sortOrder}</td>
                    <td style={{ fontSize: 20 }}>{row.icon}</td>
                    <td className="b">{row.value}</td>
                    <td>{row.label}</td>
                    <td>
                      <span className={`chip ${row.isActive ? 'ok' : 'danger'}`}>
                        {row.isActive ? 'Live' : 'Hidden'}
                      </span>
                    </td>
                    <td className="right">
                      <button className="btn ghost sm" onClick={() => setEditingStat({ ...row })}>
                        Edit
                      </button>
                      <button
                        className="btn ghost sm danger"
                        onClick={() => setRemovingStat(row)}
                        style={{ marginLeft: 4 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Testimonials</h2>
            <div className="small muted">
              Quotes shown on the registration page. Only the active ones appear, in this order.
            </div>
          </div>
          <button className="btn primary" onClick={() => setEditing({ ...BLANK })}>
            + New testimonial
          </button>
        </div>

        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <Empty message="No testimonials yet — the registration page hides the card until there is one." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Quote</th>
                  <th>Who said it</th>
                  <th>Rating</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="num dim">{row.sortOrder}</td>
                    <td style={{ maxWidth: 420 }}>
                      <div className="clamp-2">{row.quote}</div>
                    </td>
                    <td>
                      <div className="b">{row.authorName}</div>
                      {row.authorTitle ? <div className="xs muted">{row.authorTitle}</div> : null}
                    </td>
                    <td>
                      <Stars rating={row.rating} />
                    </td>
                    <td>
                      <span className={`chip ${row.isActive ? 'ok' : 'danger'}`}>
                        {row.isActive ? 'Live' : 'Hidden'}
                      </span>
                    </td>
                    <td className="right">
                      <button
                        className="btn ghost sm"
                        onClick={() =>
                          setEditing({ ...row, authorTitle: row.authorTitle ?? '' })
                        }
                      >
                        Edit
                      </button>
                      <button
                        className="btn ghost sm danger"
                        onClick={() => setRemoving(row)}
                        style={{ marginLeft: 4 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <Modal
          title={editing.id ? 'Edit testimonial' : 'New testimonial'}
          onClose={() => {
            setEditing(null);
            setErrors({});
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setEditing(null);
                  setErrors({});
                }}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={save.isPending}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending ? 'Saving…' : editing.id ? 'Save changes' : 'Add testimonial'}
              </button>
            </>
          }
        >
          <div className="form-grid">
            <div className="span2">
              <Field label="Quote" error={errors.quote} id="testimonial-quote">
                <textarea
                  id="testimonial-quote"
                  className="input tall"
                  maxLength={400}
                  placeholder="SARTHI has simplified our operations and helped us deliver a better guest experience."
                  value={editing.quote}
                  onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
                />
              </Field>
            </div>
            <TextInput
              label="Name"
              error={errors.authorName}
              placeholder="Rohit Sharma"
              value={editing.authorName}
              onChange={(e) => setEditing({ ...editing, authorName: e.target.value })}
            />
            <TextInput
              label="Title and place"
              error={errors.authorTitle}
              hint="Shown under the name."
              placeholder="Hotel Owner, Goa"
              value={editing.authorTitle}
              onChange={(e) => setEditing({ ...editing, authorTitle: e.target.value })}
            />
            <Select
              label="Rating"
              value={String(editing.rating)}
              onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })}
            >
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>
                  {'★'.repeat(n)} ({n})
                </option>
              ))}
            </Select>
            <TextInput
              label="Order"
              type="number"
              min={0}
              max={999}
              hint="Lowest first."
              error={errors.sortOrder}
              value={editing.sortOrder}
              onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
            />
            <label className="row gap-6 span2">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Show this on the registration page
            </label>
          </div>
        </Modal>
      ) : null}

      {editingStat ? (
        <Modal
          title={editingStat.id ? 'Edit figure' : 'New figure'}
          onClose={() => {
            setEditingStat(null);
            setStatErrors({});
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setEditingStat(null);
                  setStatErrors({});
                }}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={saveStat.isPending}
                onClick={() => saveStat.mutate(editingStat)}
              >
                {saveStat.isPending ? 'Saving…' : editingStat.id ? 'Save changes' : 'Add figure'}
              </button>
            </>
          }
        >
          <div className="form-grid">
            <Select
              label="Icon"
              value={editingStat.icon}
              onChange={(e) => setEditingStat({ ...editingStat, icon: e.target.value })}
            >
              {STAT_ICONS.map((glyph) => (
                <option key={glyph} value={glyph}>
                  {glyph}
                </option>
              ))}
            </Select>
            <TextInput
              label="Figure"
              error={statErrors.value}
              hint="Exactly as it should read."
              placeholder="50,000+"
              value={editingStat.value}
              onChange={(e) => setEditingStat({ ...editingStat, value: e.target.value })}
            />
            <TextInput
              label="Label"
              error={statErrors.label}
              placeholder="Rooms Managed"
              value={editingStat.label}
              onChange={(e) => setEditingStat({ ...editingStat, label: e.target.value })}
            />
            <TextInput
              label="Order"
              type="number"
              min={0}
              max={999}
              hint="Lowest first."
              error={statErrors.sortOrder}
              value={editingStat.sortOrder}
              onChange={(e) =>
                setEditingStat({ ...editingStat, sortOrder: Number(e.target.value) })
              }
            />
            <label className="row gap-6 span2">
              <input
                type="checkbox"
                checked={editingStat.isActive}
                onChange={(e) => setEditingStat({ ...editingStat, isActive: e.target.checked })}
              />
              Show this on the registration page
            </label>
          </div>
        </Modal>
      ) : null}

      {removingStat ? (
        <ConfirmDialog
          title="Remove this figure?"
          message={`"${removingStat.value} ${removingStat.label}" will disappear from the registration page. To hide it temporarily, untick "Show this" instead.`}
          confirmLabel="Remove"
          danger
          busy={removeStat.isPending}
          onConfirm={() => removeStat.mutate(removingStat)}
          onCancel={() => setRemovingStat(null)}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title="Remove this testimonial?"
          message={`"${removing.quote.slice(0, 90)}${removing.quote.length > 90 ? '…' : ''}" — ${removing.authorName}. This cannot be undone; to take it off the page temporarily, untick "Show this" instead.`}
          confirmLabel="Remove"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
